import "~/parent/eventsWs";
import { initSentry } from "~/lib/sentry";
import { WS_URL } from "~/lib/apiUrl";
import { trackProgress } from "~/lib/brobsProgress";
import { GAZE_WASM_URL } from "~/lib/gazeFiles";
import { setSentryUser, startAnalytics } from "~/parent/analytics";
import { startAuth } from "~/parent/auth";
import { showFatal } from "~/parent/fatal";
import { createGazePorts, mountGazeIframe } from "~/parent/gazeIframe";
import { handshake, type GazeInitData } from "~/parent/handshake";
import { fetchUserData, needsUsername } from "~/parent/userData";
import { mountVsCode } from "~/parent/vscode";

// The parent page starts the wasm download, the ports and the gaze iframe before auth, so that
// they load at the same time as the Auth0 session and the user data.

initSentry();
startAnalytics();

const progress = trackProgress("parent");
progress("started");

// In hot-reload mode dx serves the file, and the bootstrap checks that these
// bytes come from the link that dx serves now before it hands them to gaze.js.
const wasmUrl = import.meta.env.VITE_GAZE_HOT_RELOAD
  ? "/gaze-hot/wasm/gaze_bg.wasm"
  : GAZE_WASM_URL;
window.gazeWasmPromise = fetch(window.location.origin + wasmUrl);

const root = document.getElementById("app")!;
const ports = createGazePorts();
const gaze = mountGazeIframe(root, ports, progress);

const session = startAuth();
session.catch(async (error) => {
  console.error("Auth0 could not start:", error);
  showFatal("Membrane could not sign you in. Try reloading the page.");
  (await import("@sentry/browser")).captureException(error);
});

const token = session.then((s) => s.getToken());
const data = fetchUserData(token);
window.gazeDataPromises = {
  productSettings: data.productSettings,
  programs: data.programs,
  fileTree: data.fileTree,
};

// The `session.catch` above reports a failed session.
session.then((s) => {
  setSentryUser(s.user);
  window.__eventsWs.__start(WS_URL, s.getToken);

  // The VS Code extension reads its token through this function.
  window.globalIdeState ??= {};
  window.globalIdeState.getAuthToken = async () => {
    try {
      return await s.getToken();
    } catch (error) {
      (await import("@sentry/browser")).captureException(error);
    }
  };
}, () => {});

// Resolves when the user has a username. Otherwise the page goes to /set-username, and the
// promise does not resolve.
const userReady = data.userInfo.then(
  (info) => {
    if (needsUsername(info)) {
      window.location.href = "/set-username";
      return new Promise<never>(() => {});
    }
  },
  async (error) => {
    showFatal(
      error instanceof Error
        ? error.message
        : "Membrane could not read the user info.",
    );
    (await import("@sentry/browser")).captureException(error);
    return new Promise<never>(() => {});
  },
);

userReady.then(() => mountVsCode(root));

// No fallback to the settings blob: a product setting is a row or it is gaze's default. An empty
// answer is a user who has saved none of them, and reading the blob for one of them made saving any
// single setting look like every other one had reset.
const initData: Promise<GazeInitData> = userReady
  .then(() =>
    Promise.all([
      data.productSettings.catch((error) => {
        console.error("Failed to load product settings:", error);
        return {};
      }),
      data.programs,
    ]),
  )
  .then(([productSettings, programs]) => {
    progress("init data");
    return { productSettings, programs };
  });
initData.catch((error) => {
  console.error("Error fetching data:", error);
  showFatal("Membrane could not load the data that it needs to start. Try reloading the page.");
});

gaze.onHtmlLoaded(async (channel) => {
  try {
    const { user } = await session;
    await handshake(
      channel,
      token,
      initData,
      user,
      import.meta.env.DEV ? "bridge" : ports,
    );
    progress("init sent");
  } catch (error) {
    console.error("Error sending init message: ", error);
    showFatal("Failed to initialize Gaze communication");
  }
});
